import React, { useEffect, useRef, useState } from "react";
import { Loader2, Heart } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem("reclaim_token") || localStorage.getItem("token");
const SANS = '"Manrope", sans-serif';
const pill = "w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-[0.72rem] uppercase tracking-[0.26em]";

// After saving: a few written words back to the sender (optional), then into their library and the 21-Day set-up.
const ThankYou = ({ slug, onDone }) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    const r = await fetch(`${API}/api/public/share/${slug}/thanks-text`, {
      method: "POST", headers: { Authorization: `Bearer ${token()}`, "Content-Type": "application/json" }, body: JSON.stringify({ message: msg.trim() }),
    }).catch(() => null);
    if (r?.ok || r?.status === 409) toast.success("Your thank-you is on its way.");
    else toast.error("Couldn't send it, but your gift is saved.");
    onDone();
  };
  return (
    <div className="mt-5 rounded-2xl p-4 text-left" style={{ border: "1px solid rgba(224,169,122,0.35)" }} data-testid="share-gift-thanks-box">
      <p className="text-[0.62rem] uppercase tracking-[0.26em]" style={{ color: "#e0a97a", fontFamily: SANS }}>Saved to your library</p>
      <p className="mt-2 text-sm" style={{ color: "#e9e2d0", fontFamily: SANS }}>Would you like to send a few words of thanks?</p>
      <textarea value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={400} rows={3} placeholder="Thank you, this meant a lot…"
        className="mt-3 w-full rounded-xl bg-transparent p-3 text-sm outline-none" style={{ border: "1px solid rgba(233,226,208,0.25)", color: "#f3ecd9", fontFamily: SANS }}
        data-testid="share-gift-thanks-input" />
      <button type="button" onClick={send} disabled={busy || !msg.trim()} className={`mt-3 ${pill} disabled:opacity-50`}
        style={{ background: "#e0a97a", color: "#0b1430", fontFamily: SANS }} data-testid="share-gift-thanks-send">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Heart size={14} />} Send & open my library
      </button>
      <button type="button" onClick={onDone} className="mt-2 w-full text-xs underline-offset-4 hover:underline" style={{ color: "#bdb5a0", fontFamily: SANS }} data-testid="share-gift-thanks-skip">
        Not now, open my library
      </button>
    </div>
  );
};

// Gift recipient: keep the installation in their own Soulumo library (free sign-up first).
export const SaveGiftButton = ({ slug, label, secondary }) => {
  const [state, setState] = useState("idle");
  const [id, setId] = useState(null);
  const ran = useRef(false);
  const open = (jid) => { window.location.href = `/my-library?open=${jid}${secondary ? "" : "&plan=1"}`; };
  const save = async () => {
    if (!token()) {
      sessionStorage.setItem("soulumo_gift_slug", slug);
      window.location.href = `/soulumo/join?next=${encodeURIComponent(`/share/audio/${slug}?save=1`)}`;
      return;
    }
    setState("busy");
    try {
      const r = await fetch(`${API}/api/public/share/${slug}/save`, { method: "POST", headers: { Authorization: `Bearer ${token()}` } });
      if (!r.ok) return setState("error");
      const d = await r.json();
      if (d.own) return open(d.id);
      setId(d.id);
      setState("saved");
    } catch { setState("error"); }
  };
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (new URLSearchParams(window.location.search).get("save") === "1" && token()) save();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === "saved") return <ThankYou slug={slug} onDone={() => open(id)} />;
  return (
    <>
      <button type="button" onClick={save} disabled={state === "busy"} data-testid="share-gift-save" className={`${secondary ? "mt-3" : "mt-5"} ${pill}`}
        style={secondary ? { border: "1px solid rgba(224,169,122,0.5)", color: "#e0a97a", fontFamily: SANS } : { background: "#e0a97a", color: "#0b1430", fontFamily: SANS }}>
        {state === "busy" ? <Loader2 size={14} className="animate-spin" /> : <Heart size={14} />} {label || (token() ? "Add to my library" : "Keep it in my Soulumo library")}
      </button>
      {state === "error" && <p className="mt-2 text-xs text-center" style={{ color: "#e0857a" }} data-testid="share-gift-error">We couldn't save it just now. Please try again.</p>}
    </>
  );
};
